import type { IAppContext } from 'App/AppContext';
import { globalAppName } from 'globalConstants';
import { getFormVersionConflictCode, getFormVersionConflictText } from 'components/Tasks/Forms/formVersionConflictText';
import type { TFunction } from 'i18next';
import { DBProcesLoggerLogLevel, LogDTO } from 'models/dto/logDTO';
import { apiAddProcessLog } from 'services/Api/userService';
import { apiRequest } from 'services/Auth/authConfig';
import { AuthErrorCodes } from 'services/Auth/authErrors';
import Config from 'services/Config/configService';
import logger, { getLogWithDateStamp, globalLog } from 'services/Logging/logService';
import AppError, { normalizeError } from 'utils/appError';
import ControllerException from 'utils/controllerException';
import { safeStringify } from 'utils/stringify';
import {
  getIdentityProviderErrorCode,
  isAbortOrCancelError,
  isAuthReloadError,
  isLoginError,
  isNetworkError,
  isTenantRestoreFailedError,
  isTenantRestoreInProgressError,
  isTenantSuspendedError,
  isTooManyRequestsError,
} from './ErrorMessageHelper';

export const getErrorMessageText = (error: AppError, t: TFunction<string[]>): string => {
  //a refusal of the identity layer arrives as a 401 with an IDP code; its own text says what to do
  //(sign in with a work account, with an account of the organization's directory) where the generic
  //"insufficient permissions" would not
  const identityProviderCode = getIdentityProviderErrorCode(error);
  if (identityProviderCode) {
    return ControllerException.getMessage(identityProviderCode, t);
  }

  //the errors the app's own identity layer raises: a Microsoft feature for a user of another provider, and
  //the two outcomes of a Google sign-in that needs the person or their admin
  switch (error.code) {
    case AuthErrorCodes.MicrosoftIdentityRequired:
      return t('identityproviders:Errors.MicrosoftIdentityRequired');
    case AuthErrorCodes.OidcAdminPolicy:
      return t('identityproviders:Errors.AdminPolicy', { appName: globalAppName });
    case AuthErrorCodes.OidcSignInAgain:
      return t('identityproviders:Errors.SignInAgain');
    case AuthErrorCodes.OidcIssuerUnknown:
      return t('identityproviders:Errors.IssuerUnknown');
  }

  if (isTenantRestoreInProgressError(error)) {
    return t('translation:Errors.RestoreInProgress.Message');
  }

  //the three tenant-lock states arrive as a 401 whose body names the state; without these the user reads
  //"insufficient permissions" for a tenant that is being restored, failed to restore, or is suspended
  if (isTenantRestoreFailedError(error)) {
    return t('translation:Errors.RestoreFailed.Message');
  }

  if (isTenantSuspendedError(error)) {
    return t('translation:Errors.TenantSuspended.Message');
  }

  if (isNetworkError(error) || isAbortOrCancelError(error) || isLoginError(error)) {
    return t('translation:Errors.NetworkError.Message');
  }

  if (isTooManyRequestsError(error)) {
    return t('translation:Errors.TooManyRequests.Message');
  }

  switch (error.code) {
    case '404':
      return t('translation:Errors.DatabaseNotFound.Message');
    case '409': {
      // A form versioning refusal carries its own code and text.
      const formConflictText = getFormVersionConflictText(getFormVersionConflictCode(error), t);
      if (formConflictText) {
        return formConflictText;
      }

      // A typed conflict (the API attached a ControllerException code) has its own
      // translated message; only the untyped 409 is the generic concurrency conflict.
      if (AppError.isControllerException(error)) {
        return AppError.getControllerExceptionMsg(error, t) ?? error.message;
      }

      return t('translation:Errors.DatabaseConflict.Message');
    }
    case '401':
    case '403':
      return t('translation:Errors.AccessDenied.Message');
    case '402':
      // The AI credits are used up (CREDIT0001): the translated text names where to buy credits.
      return AppError.getControllerExceptionMsg(error, t) ?? error.message;
    case '400':
    case '500':
      if (error.debug && error.debug.indexOf('AccessDenied') >= 0) {
        return t('translation:Errors.AccessDenied.Message');
      }

      if (AppError.isControllerException(error)) {
        return AppError.getControllerExceptionMsg(error, t) ?? error.message;
      }

      return t('translation:Errors.ApiError.Message');
    case '999':
      return 'An unknown fatal error has occurred. We are sorry for the inconvenience! Please reload your browser and contact our Support Desk (support@isoplanner.app) when the problem persists.';
    default:
      return error.message;
  }
};

export const saveErrorToApi = async (
  error: AppError | undefined,
  appContext: IAppContext,
  t: TFunction<string[]>,
): Promise<boolean> => {
  try {
    if (!error) {
      return false;
    }

    // A connectivity failure means the API is unreachable by definition, so uploading
    // the log is guaranteed to fail and only produces more noise (a failed POST plus a
    // cascading upload error). Skip the attempt entirely.
    if (isNetworkError(error)) {
      return false;
    }

    // While a restore runs, the tenant is suspended and the API rejects every call -- including this
    // log upload. Same for a suspended tenant. In both cases uploading is guaranteed to fail and would
    // only pile an upload-error toast on top of the message the user already sees.
    if (isTenantRestoreInProgressError(error) || isTenantRestoreFailedError(error) || isTenantSuspendedError(error)) {
      return false;
    }

    // A read-only checkpoint preview session rejects every POST by middleware, so the upload can
    // never succeed while previewing a backup.
    if (appContext.user?.login?.isCheckpointPreview) {
      return false;
    }

    const logDTO = new LogDTO(
      error.fromApi === true
        ? `Global client log for API exception ${error.apiLogId || 0}`
        : error.message ?? 'Unknown error',
    );

    logDTO.error = safeStringify(error);
    logDTO.add(getLogWithDateStamp('Environment info: ' + Config.getEnvInfo()));
    logDTO.add(getLogWithDateStamp(error.stack));
    logDTO.add(getLogWithDateStamp(error.debug));
    logDTO.add('Global log entries');
    globalLog.messages.forEach((message) => logDTO.add(message));

    const filterMsg: string[] = [
      'access_denied',
      'interaction_in_progress',
      'user_cancelled',
      'consent_required',
      'popup_window_error',
      'hash_empty_error',
      'Authentication_MSGraphPermissionMissing',
      'Authorization_RequestDenied',
      'InvalidAuthenticationToken',
      'Authorization_IdentityNotFound',
      'NoPermissionsInAccessToken',
      'AADSTS7000229',
      'AADSTS500011',
      'AADSTS50173',
      'AADSTS650051',
      'AADSTS70008',
    ];

    const filterBackEndStatusCodes: string[] = ['403', '404', '409'];
    const isRateLimited = isTooManyRequestsError(error);

    let shouldNotCreateTicket =
      !isRateLimited &&
      (error.fromApi === true ||
        isNetworkError(error) ||
        isAuthReloadError(error) ||
        isAbortOrCancelError(error) ||
        (error.code ? filterBackEndStatusCodes.includes(error.code) : false));

    if (shouldNotCreateTicket === false) {
      // Entries are matched case-insensitively against error.message and error.debug
      shouldNotCreateTicket = filterMsg.some((message) => {
        if (error.code === message) {
          return true;
        }

        const needle = message.toLowerCase();

        if (error.message && error.message.toLowerCase().indexOf(needle) >= 0) {
          return true;
        }

        if (error.debug && error.debug.toLowerCase().indexOf(needle) >= 0) {
          return true;
        }

        return false;
      });
    }

    if (isRateLimited) {
      logDTO.name = 'Global client log for API exception - Rate limit exceeded';
      logDTO.level = DBProcesLoggerLogLevel.Warning;
    } else if (shouldNotCreateTicket) {
      logDTO.level = DBProcesLoggerLogLevel.Info;
    } else {
      logDTO.level = DBProcesLoggerLogLevel.Error;
    }

    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    await apiAddProcessLog(logDTO, accessToken);
    globalLog.clear();

    return true;
  } catch (err) {
    // If the upload itself failed because the API is unreachable (backend down or the client is
    // offline) or because a tenant restore started between the original error and this upload, there
    // is nothing the user can do about it and re-surfacing it only piles a scary notification on top
    // of the original error. Log quietly and stop.
    const uploadError = normalizeError(err);
    if (isNetworkError(uploadError) || isTenantRestoreInProgressError(uploadError)) {
      logger.debug('Skipped uploading client log to API: API not available');

      return false;
    }

    logger.error(new AppError('Error while uploading log to API. ' + safeStringify(err)));
    appContext.showNotification(t('translation:Errors.UploadClientLog.Message'), true);

    return false;
  }
};
